/**
 * MEXC WebSocket API
 *
 * Base WebSocket configuration and utilities for MEXC exchange.
 * MEXC uses protobuf-encoded binary messages for market data streams.
 *
 * @module cex-sdk/exchanges/mexc/ws
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import { decodeMexcMessage } from './proto';
import type { MexcWsAckResponse, MexcWsSubscribeRequest, PushDataV3ApiWrapper } from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const MEXC_WS_BASE_URL = 'wss://wbs-api.mexc.com/ws';

/**
 * MEXC WebSocket channel descriptors.
 * All channels use protobuf binary encoding (indicated by .pb suffix).
 *
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams
 */
export const MEXC_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'spot@public.aggre.depth.v3.api.pb@<interval>@<SYMBOL>',
    supportsBatching: true,
    docUrl:
      'https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams-aggregate-order-book',
  },
  ticker: {
    channel: 'ticker',
    topic: 'spot@public.miniTicker.v3.api.pb@<SYMBOL>@<timezone>',
    supportsBatching: true,
    docUrl:
      'https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams-mini-ticker',
  },
  klines: {
    channel: 'klines',
    topic: 'spot@public.kline.v3.api.pb@<SYMBOL>@<interval>',
    supportsBatching: true,
    docUrl:
      'https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams-kline-candlestick',
  },
  trades: {
    channel: 'trades',
    topic: 'spot@public.aggre.deals.v3.api.pb@<interval>@<SYMBOL>',
    supportsBatching: true,
    docUrl:
      'https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams-aggregate-trades',
  },
  bookTicker: {
    channel: 'bookTicker',
    topic: 'spot@public.aggre.bookTicker.v3.api.pb@<interval>@<SYMBOL>',
    supportsBatching: true,
    docUrl:
      'https://mexcdevelop.github.io/apidocs/spot_v3_en/#websocket-market-streams-aggregate-book-ticker',
  },
};

/**
 * MEXC WebSocket topology information.
 * CRITICAL: All market data messages are protobuf-encoded binary.
 * JSON is only used for subscription requests/responses and ping/pong.
 */
export const MEXC_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: MEXC_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 60_000, // Client must send ping every 60s
  note: 'All market data uses protobuf encoding. JSON ping/pong required every 60s. Subscription via JSON method: SUBSCRIPTION.',
};

// ========================================
// Subscription Utilities
// ========================================

export type MexcWsStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

export interface MexcWsSubscriptionOptions {
  /** Full channel name to subscribe to */
  channel: string;
  /** Callback for successfully decoded protobuf messages */
  onMessage: (data: PushDataV3ApiWrapper) => void;
  /** Callback for errors (connection, decode, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: MexcWsStatus) => void;
  /** Base WebSocket URL (defaults to MEXC_WS_BASE_URL) */
  baseUrl?: string;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

/**
 * Create a MEXC WebSocket subscription.
 * Handles protobuf decoding, JSON ping/pong, and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 *
 * @example
 * ```typescript
 * const ws = subscribeMexcChannel({
 *   channel: 'spot@public.aggre.deals.v3.api.pb@100ms@BTCUSDT',
 *   onMessage: (data) => {
 *     if (data.publicAggreDeals) {
 *       console.log('Trades:', data.publicAggreDeals.dealsList);
 *     }
 *   },
 *   onStatusChange: (status) => console.log('Status:', status),
 * });
 *
 * // Later: ws.close()
 * ```
 */
export function subscribeMexcChannel(options: MexcWsSubscriptionOptions): ReconnectingWebSocket {
  const {
    channel,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = MEXC_WS_BASE_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);
  // IMPORTANT: MEXC sends binary protobuf data
  ws.binaryType = 'arraybuffer';

  const sendPing = () => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ method: 'PING' }));
    }
  };

  const startPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    // Send ping every 30 seconds (half of 60s timeout for safety)
    pingInterval = setInterval(sendPing, 30_000);
  };

  const stopPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
  };

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Send subscription request
    const subscribeMsg: MexcWsSubscribeRequest = {
      method: 'SUBSCRIPTION',
      params: [channel],
    };

    ws.send(JSON.stringify(subscribeMsg));

    // Start ping interval
    startPingInterval();
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    // Handle JSON messages (subscription ack, pong)
    if (typeof event.data === 'string') {
      try {
        const jsonData = JSON.parse(event.data) as MexcWsAckResponse;

        // Check for subscription errors
        if (jsonData.code !== undefined && jsonData.code !== 0) {
          const error = new Error(
            `MEXC subscription error: code=${jsonData.code}, msg="${jsonData.msg}"`,
          );
          onError?.(error);
          return;
        }

        // PONG or subscription confirmation - ignore
        return;
      } catch (parseError) {
        // Not valid JSON, shouldn't happen
        onError?.(new Error(`MEXC unexpected string message: ${event.data}`));
        return;
      }
    }

    // Handle binary protobuf messages
    if (event.data instanceof ArrayBuffer) {
      try {
        const message = decodeMexcMessage(new Uint8Array(event.data));

        if (!message) {
          // Decoder not ready or decode failed - already logged by decoder
          return;
        }

        // Verify this message is for our channel
        if (message.channel !== channel) {
          return;
        }

        onMessage(message);
      } catch (decodeError) {
        const error = decodeError instanceof Error ? decodeError : new Error(String(decodeError));
        onError?.(error);
      }
      return;
    }

    // Handle Blob (convert to ArrayBuffer)
    if (event.data instanceof Blob) {
      event.data
        .arrayBuffer()
        .then(buffer => {
          const message = decodeMexcMessage(new Uint8Array(buffer));

          if (!message || message.channel !== channel) {
            return;
          }

          onMessage(message);
        })
        .catch(blobError => {
          const error = blobError instanceof Error ? blobError : new Error(String(blobError));
          onError?.(error);
        });
    }
  });

  ws.addEventListener('close', () => {
    stopPingInterval();
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`MEXC WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  // Override close to cleanup ping interval
  const originalClose = ws.close.bind(ws);
  ws.close = (code?: number, reason?: string) => {
    stopPingInterval();

    // Send unsubscription message if still connected
    if (ws.readyState === WebSocket.OPEN) {
      const unsubscribeMsg: MexcWsSubscribeRequest = {
        method: 'UNSUBSCRIPTION',
        params: [channel],
      };
      ws.send(JSON.stringify(unsubscribeMsg));
    }

    originalClose(code, reason);
  };

  return ws;
}

// ========================================
// Channel Builders
// ========================================

/**
 * Build MEXC orderbook depth channel name.
 *
 * @example
 * buildMexcDepthChannel('BTCUSDT', '100ms') // 'spot@public.aggre.depth.v3.api.pb@100ms@BTCUSDT'
 */
export function buildMexcDepthChannel(symbol: string, interval = '100ms'): string {
  return `spot@public.aggre.depth.v3.api.pb@${interval}@${symbol.toUpperCase()}`;
}

/**
 * Build MEXC trades channel name.
 *
 * @example
 * buildMexcTradesChannel('BTCUSDT', '100ms') // 'spot@public.aggre.deals.v3.api.pb@100ms@BTCUSDT'
 */
export function buildMexcTradesChannel(symbol: string, interval = '100ms'): string {
  return `spot@public.aggre.deals.v3.api.pb@${interval}@${symbol.toUpperCase()}`;
}

/**
 * Build MEXC kline channel name.
 *
 * @param symbol Trading pair symbol (e.g., 'BTCUSDT')
 * @param interval Kline interval: 'Min1', 'Min5', 'Min15', 'Min60', 'Hour4', 'Day1', 'Week1'
 *
 * @example
 * buildMexcKlineChannel('BTCUSDT', 'Min1') // 'spot@public.kline.v3.api.pb@BTCUSDT@Min1'
 */
export function buildMexcKlineChannel(symbol: string, interval: string): string {
  return `spot@public.kline.v3.api.pb@${symbol.toUpperCase()}@${interval}`;
}

/**
 * Build MEXC ticker channel name.
 *
 * @param symbol Trading pair symbol (e.g., 'BTCUSDT')
 * @param timezone Optional timezone (defaults to UTC+0)
 *
 * @example
 * buildMexcTickerChannel('BTCUSDT') // 'spot@public.miniTicker.v3.api.pb@BTCUSDT@UTC+0'
 */
export function buildMexcTickerChannel(symbol: string, timezone = 'UTC+0'): string {
  return `spot@public.miniTicker.v3.api.pb@${symbol.toUpperCase()}@${timezone}`;
}

/**
 * Build MEXC book ticker channel name.
 *
 * @example
 * buildMexcBookTickerChannel('BTCUSDT', '100ms') // 'spot@public.aggre.bookTicker.v3.api.pb@100ms@BTCUSDT'
 */
export function buildMexcBookTickerChannel(symbol: string, interval = '100ms'): string {
  return `spot@public.aggre.bookTicker.v3.api.pb@${interval}@${symbol.toUpperCase()}`;
}

// ========================================
// Interval Mapping
// ========================================

/**
 * Map TradingView/widget resolution to MEXC WebSocket kline interval.
 *
 * MEXC WebSocket supported intervals:
 * - Min1, Min5, Min15, Min30, Min60 (1m, 5m, 15m, 30m, 1h)
 * - Hour4, Hour8 (4h, 8h - NO Hour2, Hour6, Hour12!)
 * - Day1, Week1, Month1 (1d, 1w, 1M - NO Day2, Day3, Day5!)
 *
 * @see https://www.mexc.com/api-docs/spot-v3/websocket-market-streams
 *
 * @example
 * mapResolutionToMexcKlineInterval('1') // 'Min1'
 * mapResolutionToMexcKlineInterval('60') // 'Min60'
 * mapResolutionToMexcKlineInterval('240') // 'Hour4'
 * mapResolutionToMexcKlineInterval('1D') // 'Day1'
 */
export function mapResolutionToMexcKlineInterval(resolution: string): string {
  const upper = resolution.toUpperCase();

  // Handle day/week/month special cases
  if (upper === 'D' || upper === '1D') return 'Day1';
  if (upper === 'W' || upper === '1W') return 'Week1';
  if (upper === 'M' || upper === '1M') return 'Month1';

  // Parse numeric resolution (assumes minutes)
  const minutes = parseInt(resolution, 10);
  if (isNaN(minutes)) return 'Min1';

  // Exact matches for supported intervals
  if (minutes === 1) return 'Min1';
  if (minutes === 5) return 'Min5';
  if (minutes === 15) return 'Min15';
  if (minutes === 30) return 'Min30';
  if (minutes === 60) return 'Min60';
  if (minutes === 240) return 'Hour4';
  if (minutes === 480) return 'Hour8';
  if (minutes === 1440) return 'Day1';
  if (minutes === 10080) return 'Week1';

  // For unsupported intervals, find closest supported one
  // MEXC WS intervals in minutes: 1, 5, 15, 30, 60, 240, 480, 1440, 10080
  const supportedMinutes = [1, 5, 15, 30, 60, 240, 480, 1440, 10080];
  const intervalNames: Record<number, string> = {
    1: 'Min1',
    5: 'Min5',
    15: 'Min15',
    30: 'Min30',
    60: 'Min60',
    240: 'Hour4',
    480: 'Hour8',
    1440: 'Day1',
    10080: 'Week1',
  };

  // Find closest supported interval
  let closest = supportedMinutes[0];
  let closestDiff = Math.abs(minutes - closest);

  for (const supported of supportedMinutes) {
    const diff = Math.abs(minutes - supported);
    if (diff < closestDiff) {
      closest = supported;
      closestDiff = diff;
    }
  }

  return intervalNames[closest];
}

/**
 * Parse MEXC kline interval to milliseconds.
 *
 * @example
 * parseMexcKlineIntervalToMs('Min1') // 60000
 * parseMexcKlineIntervalToMs('Hour4') // 14400000
 * parseMexcKlineIntervalToMs('Hour8') // 28800000
 */
export function parseMexcKlineIntervalToMs(interval: string): number {
  const intervalMap: Record<string, number> = {
    Min1: 60_000,
    Min5: 300_000,
    Min15: 900_000,
    Min30: 1_800_000,
    Min60: 3_600_000,
    Hour4: 14_400_000,
    Hour8: 28_800_000,
    Day1: 86_400_000,
    Week1: 604_800_000,
    Month1: 2_592_000_000, // ~30 days
  };

  return intervalMap[interval] ?? 60_000; // default to 1 minute
}
